import React, { useContext } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { getContext } from '@fiverr-private/fiverr_context';
import { useImpressionItem, useImpressionContainer } from '@fiverr-private/impressionable';
import { Grid, GridItem, Stack } from '@fiverr-private/layout_components';
import {
    UserData,
    BuyerInsights,
    ratiosEqual,
    XL,
    EnrichedImpressionData,
    PrimaryPositionEnum,
} from '@fiverr-private/homepage_common_types';
import { IPrimaryArgs } from '@fiverr-private/primary_section_sdk';
import { getBuyerState, onImpression } from '@fiverr-private/homepage_utils';
import { useUser } from '../../context/user/userProvider';
import { ThemeContext } from '../../context/theme/theme.context';
import { PrimaryMapper } from '../../../lib/utils/primaryMapper';
import { hideScrollbars } from '../../shared/sharedStyles.ve.css';
import { PrimaryProps } from './PrimarySection.types';
import { usePrimaryEnrichedData, calcGridColumnPlacement, usePrimaryRatio } from './utils';
import PrimaryComponent from './components/PrimaryComponent';
import { XLPrimaryGridItemStyles, primaryGridItemStyles } from './constants';

const PrimarySection = (props: PrimaryProps): React.JSX.Element => {
    const { ratio: originalRatio, primariesToDisplay, position } = props;
    const context = getContext();
    const { buyerInsights, userData } = useUser();
    const { layoutId } = useContext(ThemeContext);
    const [ref, containerId] = useImpressionContainer();
    const buyerState = getBuyerState(buyerInsights?.segment);
    const hasTwoPrimaries = !ratiosEqual(originalRatio, XL);
    const { ratio } = usePrimaryRatio(originalRatio, hasTwoPrimaries);
    let componentName;
    let containerName;

    switch (position) {
        case PrimaryPositionEnum.PRIMARY:
            componentName = 'primary';
            containerName = 'primaries-container';
            break;
        case PrimaryPositionEnum.SECONDARY:
            componentName = 'secondary';
            containerName = 'secondaries-container';
            break;
        case PrimaryPositionEnum.TERNARY:
            componentName = 'ternary';
            containerName = 'ternaries-container';
            break;
        default:
            const _exhaustiveCheck: never = position;

            break;
    }

    useImpressionItem({
        selfRef: ref,
        selfId: containerId,
        sendImpressionToBQ: false,
        enrichment: {
            layoutId,
            buyerState,
            componentName: containerName,
            componentType: containerName,
        },
        onImpression: async (impressionData: EnrichedImpressionData) => {
            await onImpression(impressionData);
        },
    });

    const enrichPrimaryDataArgs: IPrimaryArgs = {
        context,
        buyerInsights: buyerInsights as BuyerInsights,
        userData: userData as UserData,
    };

    const [leftPrimarySize, rightPrimarySize] = ratio;
    const { leftPrimary: leftPrimaryData, rightPrimary: rightPrimaryData } = primariesToDisplay;

    const {
        enrichPrimaryData: enrichLeftPrimaryData,
        enrichFallbackPrimaryData: enrichLeftFallbackPrimaryData,
        PrimaryComponent: LeftPrimaryComponentSuccess,
    } = PrimaryMapper[leftPrimaryData?.type] ?? {};
    const {
        enrichPrimaryData: enrichRightPrimaryData,
        enrichFallbackPrimaryData: enrichRightFallbackPrimaryData,
        PrimaryComponent: RightPrimaryComponentSuccess,
    } = PrimaryMapper[rightPrimaryData?.type] ?? {};

    const leftPrimaryEnrichedData = usePrimaryEnrichedData(
        'PRIMARY_LEFT',
        enrichPrimaryDataArgs,
        leftPrimaryData,
        leftPrimarySize,
        enrichLeftPrimaryData,
        enrichLeftFallbackPrimaryData,
        ratio,
        buyerInsights.audiences
    );
    const rightPrimaryEnrichedData = usePrimaryEnrichedData(
        'PRIMARY_RIGHT',
        enrichPrimaryDataArgs,
        rightPrimaryData,
        rightPrimarySize,
        enrichRightPrimaryData,
        enrichRightFallbackPrimaryData,
        ratio,
        buyerInsights.audiences
    );

    const queryClient = new QueryClient();

    return (
        <Stack
            gap="6"
            direction="column"
            overflow="auto"
            width="100%"
            data-testid={`${componentName}-section`}
            marginBottom="12"
        >
            <QueryClientProvider client={queryClient}>
                <Grid
                    ref={ref}
                    templateColumns="12_cols"
                    gap={{ default: '4', xl: '8' }}
                    overflow="auto"
                    className={hideScrollbars}
                    data-testid={`${componentName}-grid`}
                >
                    <GridItem
                        columnStart="1"
                        columnEnd={calcGridColumnPlacement(ratio)}
                        {...(ratiosEqual(ratio, XL) ? XLPrimaryGridItemStyles : primaryGridItemStyles)}
                        data-testid={`${componentName}-left-component`}
                    >
                        <PrimaryComponent
                            PrimaryComponent={LeftPrimaryComponentSuccess}
                            primaryEnrichedData={leftPrimaryEnrichedData}
                            componentName={componentName}
                        />
                    </GridItem>
                    {hasTwoPrimaries && (
                        <GridItem
                            columnStart={calcGridColumnPlacement(ratio)}
                            columnEnd="13"
                            {...primaryGridItemStyles}
                            data-testid={`${componentName}-right-component`}
                        >
                            <PrimaryComponent
                                PrimaryComponent={RightPrimaryComponentSuccess}
                                primaryEnrichedData={rightPrimaryEnrichedData}
                                componentName={componentName}
                            />
                        </GridItem>
                    )}
                </Grid>
            </QueryClientProvider>
        </Stack>
    );
};

export default PrimarySection;
